import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Copy, Mail, Send, RefreshCw } from "lucide-react";

const AiEmailDialog = ({ doc, onClose, title, basePath, modes, fromEmail = "sales@hitechsilico.com" }) => {
  const [mode, setMode] = useState(modes[0].key);
  const [draft, setDraft] = useState(null);
  const [instructions, setInstructions] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const active = modes.find((m) => m.key === mode) || modes[0];

  const generate = async (m = mode) => {
    setBusy(true);
    try {
      const r = await api.post(`${basePath}/${doc.id}/ai-email`, { mode: m, instructions });
      setDraft(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const copyAll = async () => {
    await navigator.clipboard.writeText(`Subject: ${draft.subject}\n\n${draft.body}`);
    toast.success("Email copied — paste it into Zoho Mail");
  };

  const mailto = () => {
    const url = `mailto:${encodeURIComponent(draft.to || "")}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`;
    window.open(url, "_blank");
  };

  const sendZoho = async () => {
    if (!window.confirm(`Send this email (with ${active.attach} attached) from ${fromEmail} to ${draft.to}?`)) return;
    setSending(true);
    try {
      const r = await api.post(`${basePath}/${doc.id}/send-zoho`, { subject: draft.subject, body: draft.body, doc_type: active.docType });
      toast.success(`Sent to ${r.data.to} with PDF attached`);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    setSending(false);
  };

  if (!doc) return null;
  return (
    <Dialog open={!!doc} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="quote-ai-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={16} className="text-[#3B82F6]" /> {title}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          {modes.map(({ key, label }) => (
            <button key={key} onClick={() => { setMode(key); setDraft(null); }}
                    className={`text-xs font-semibold rounded-md px-3 py-1.5 border transition-colors duration-150 ${mode === key ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}
                    data-testid={`ai-email-mode-${key}`}>{label}</button>
          ))}
          <button onClick={() => generate()} disabled={busy}
                  className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#3B82F6] hover:bg-[#2563EB] rounded-md px-3.5 py-2 disabled:opacity-50"
                  data-testid="ai-email-generate">
            {busy ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {draft ? "Regenerate" : "Generate Email"}
          </button>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Instructions for the AI (optional)</label>
          <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={2}
                    placeholder={'e.g. "mention 50% advance required", "keep it short, skip the item list", "mention dispatch in 10 days"'}
                    className="mt-1 text-[13px]" data-testid="ai-email-instructions" />
        </div>
        {!draft && <p className="text-sm text-slate-400 py-6 text-center">{busy ? "Writing your email…" : "Choose the type, add any instructions, and hit Generate — the AI drafts a complete, ready-to-send email."}</p>}
        {draft && (
          <div className="space-y-3">
            <div className="text-xs text-slate-500">To: <b className="text-slate-800">{draft.to || "no email on file"}</b>{draft.cc?.length > 0 && <> · Cc: {draft.cc.join(", ")}</>}</div>
            <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="font-semibold" data-testid="ai-email-subject" />
            <Textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={14} className="text-[13px] leading-relaxed" data-testid="ai-email-body" />
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={copyAll} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-200 rounded-md px-3 py-2 text-slate-600 hover:border-slate-400" data-testid="ai-email-copy"><Copy size={12} /> Copy for Zoho</button>
              <button onClick={mailto} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-200 rounded-md px-3 py-2 text-slate-600 hover:border-slate-400" data-testid="ai-email-mailto"><Mail size={12} /> Open in Mail App</button>
              {draft.can_send ? (
                <button onClick={sendZoho} disabled={sending || !draft.to}
                        className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md px-4 py-2 disabled:opacity-50"
                        data-testid="ai-email-send-zoho">
                  <Send size={12} /> {sending ? "Sending…" : `Send via Zoho (${active.attach} attached)`}
                </button>
              ) : (
                <span className="ml-auto text-[11px] text-slate-400">Direct send available once Zoho app password is configured</span>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const QuoteAiEmailDialog = ({ quote, onClose }) => (
  <AiEmailDialog doc={quote} onClose={onClose} basePath="/quotations"
                 title={quote ? `AI Email — Quotation ${quote.number}` : ""}
                 modes={[
                   { key: "initial", label: "Send Quotation", attach: "Quotation PDF", docType: "quotation" },
                   { key: "followup", label: "Follow-up Reminder", attach: "Quotation PDF", docType: "quotation" },
                 ]} />
);

export const ProformaAiEmailDialog = ({ quote, onClose }) => (
  <AiEmailDialog doc={quote} onClose={onClose} basePath="/quotations"
                 title={quote ? `AI Email — Pro Forma ${quote.proforma_number}` : ""}
                 modes={[
                   { key: "proforma", label: "Pro Forma Invoice", attach: "Pro Forma PDF", docType: "proforma" },
                 ]} />
);

export const InvoiceAiEmailDialog = ({ invoice, onClose }) => (
  <AiEmailDialog doc={invoice} onClose={onClose} basePath="/invoices" fromEmail="accounts@hitechsilico.com"
                 title={invoice ? `AI Email — Invoice ${invoice.number}` : ""}
                 modes={[
                   { key: "initial", label: "Send Invoice", attach: "Invoice PDF", docType: "invoice" },
                   { key: "followup", label: "Payment Reminder", attach: "Invoice PDF", docType: "invoice" },
                 ]} />
);
